(() => {
  if (window.FAFSearchCards) return;
  const states = new WeakMap();
  const normalize = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\bgrey\b/g, 'gray');
  const values = input => new Set((Array.isArray(input) ? input : []).map(normalize).filter(Boolean));
  const preferred = variants => variants.find(variant => variant.available) || variants[0] || null;

  function optionIndex(data, expression) {
    const option = data.options.find(item => expression.test(normalize(item.name)));
    return option ? Number(option.position) - 1 : -1;
  }

  function initialSelection(data, plan) {
    if (data.hasOnlyDefaultVariant) return {status:'not-applicable', variant:null, constraintTypes:[]};
    // Use the same eligible configuration as the search matcher so the card's
    // photo, price and product link agree with the requested size or comfort.
    if (plan.postFilter && typeof window.FAFSearch?.matchingVariants === 'function') {
      const matching = window.FAFSearch.matchingVariants(data, plan);
      if (Array.isArray(matching)) {
        const constraintTypes = [
          ...(plan.colors?.length || plan.facetOptions?.some(option => /^(?:colou?r|finish)$/.test(option.name) && option.values?.length) ? ['color'] : []),
          ...(plan.sizes?.length || plan.facetOptions?.some(option => option.name === 'size' && option.values?.length) ? ['size'] : []),
        ];
        if (!matching.length) return {status:'incompatible', variant:null, constraintTypes};
        if (constraintTypes.length || matching.length !== data.variants.length) {
          const variant = matching.find(item => item.available && String(item.id) === String(data.defaultVariantId)) || preferred(matching);
          return {status:'matched', variant, constraintTypes};
        }
      }
    }
    const constraints = [];
    const colors = values(plan.colors);
    const sizes = values(plan.sizes);
    const colorIndex = optionIndex(data, /\b(colou?r|finish)\b/);
    const sizeIndex = optionIndex(data, /\bsize\b/);
    if (colorIndex >= 0 && colors.size) constraints.push([colorIndex, colors, 'color']);
    if (sizeIndex >= 0 && sizes.size) constraints.push([sizeIndex, sizes, 'size']);
    if (!constraints.length) return {status:'not-applicable', variant:null, constraintTypes:[]};
    const variant = preferred(data.variants.filter(item => constraints.every(([index, choices]) => choices.has(normalize(item.options[index])))));
    return {status:variant ? 'matched' : 'incompatible', variant, constraintTypes:constraints.map(item => item[2])};
  }

  function variantURL(data, variant) {
    const url = new URL(data.url, document.baseURI);
    if (!/^https?:$/.test(url.protocol)) return null;
    url.searchParams.set('variant', variant.id);
    const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
    if (previewTheme && /^\d+$/.test(previewTheme) && url.origin === location.origin) url.searchParams.set('preview_theme_id', previewTheme);
    return url.origin === new URL(document.baseURI).origin ? `${url.pathname}${url.search}${url.hash}` : url.href;
  }

  function sameOtherOptions(variant, selected, changedIndex) {
    return selected.options.every((value, index) => index === changedIndex || normalize(variant.options[index]) === normalize(value));
  }

  function swatchVariant(state, input) {
    const position = Number(input.closest('[data-option-position]')?.dataset.optionPosition) - 1;
    if (!Number.isInteger(position) || position < 0) return null;
    return preferred(state.data.variants.filter(variant => normalize(variant.options[position]) === normalize(input.value) && sameOtherOptions(variant, state.current, position)));
  }

  function syncSwatches(state) {
    state.card.querySelectorAll('.js-color-swatches input[type="radio"]').forEach(input => {
      const position = Number(input.closest('[data-option-position]')?.dataset.optionPosition) - 1;
      if (!Number.isInteger(position) || position < 0) return;
      input.checked = normalize(input.value) === normalize(state.current.options[position]);
      const variant = swatchVariant(state, input);
      input.disabled = !variant;
      input.classList.toggle('disabled', !variant || !variant.available);
      input.setAttribute('aria-disabled', String(!variant));
      if (variant) {
        input.dataset.variantId = variant.id;
        const url = variantURL(state.data, variant);
        if (url) input.dataset.variantLink = url;
      } else {
        delete input.dataset.variantId;
        delete input.dataset.variantLink;
      }
    });
  }

  function updateImage(state, variant) {
    const image = state.card.querySelector('.card__inner .media--first, .predictive-search__image');
    state.photoWarning = false;
    if (!image) return;
    state.card.querySelectorAll('.card__inner .media--second').forEach(secondary => secondary.remove());
    image.closest('.media')?.classList.remove('media--hover-effect');
    image.style.objectFit = 'contain';
    image.style.objectPosition = 'center';
    image.style.opacity = '1';
    image.style.transform = 'none';
    const asset = variant.image || state.data.image;
    if (!variant.image?.url && state.colorSelectionActive && state.colorIndex >= 0) {
      const identity = value => {
        if (!value) return null;
        try { const url = new URL(value, document.baseURI); return url.origin + url.pathname; } catch { return null; }
      };
      const mainImage = identity(state.data.image?.url);
      const verifiedColor = mainImage && state.data.variants.some(item => item.image?.url && identity(item.image.url) === mainImage && normalize(item.options[state.colorIndex]) === normalize(variant.options[state.colorIndex]));
      state.photoWarning = !verifiedColor;
    }
    if (!asset?.url) return;
    let url;
    try {
      url = new URL(asset.url, document.baseURI);
      if (!/^https?:$/.test(url.protocol)) return;
    } catch { return; }
    const widths = [165, 360, 533, 720, 940, 1066, 1200].filter(width => !asset.width || width <= asset.width);
    image.srcset = widths.map(width => {
      const source = new URL(url.href);
      source.searchParams.set('width', String(width));
      return `${source.href} ${width}w`;
    }).join(', ');
    if (image.classList.contains('predictive-search__image')) {
      image.sizes = '90px';
    } else {
      if (asset.width > 0) image.width = asset.width;
      if (asset.height > 0) image.height = asset.height;
    }
    image.alt = asset.alt || `${state.data.title} — ${variant.title}`;
    image.src = url.href;
  }

  function text(element, className, value) {
    const node = document.createElement(element);
    if (className) node.className = className;
    node.textContent = value ?? '';
    return node;
  }

  function updatePrice(state, variant) {
    const price = state.card.querySelector('.card-information .price--card, .predictive-search__item-content .price');
    if (!price) return;
    price.classList.add('price--card');
    const sale = Number(variant.compareAtPrice) > Number(variant.price);
    price.classList.toggle('price--on-sale', sale);
    price.classList.toggle('price--sold-out', !variant.available);
    price.classList.remove('price--no-compare', 'price--unavailable');
    const regular = price.querySelector('.price__regular .price-item--regular');
    const final = price.querySelector('.price-item--sale');
    if (regular) regular.replaceChildren(text('span', 'price__amount', variant.priceFormatted));
    if (final) final.replaceChildren(text('span', 'price__amount', variant.priceFormatted));
    const original = price.querySelector('.price__card-original');
    if (original) {
      const nodes = [text('s', 'price-item price-item--regular price__compare', variant.compareAtPriceFormatted)];
      if (sale && variant.available && state.data.showSaleBadge && Number(variant.percentOff) > 0) {
        nodes.push(text('span', 'badge badge--sale card-information__sale-badge', variant.percentOffText));
      }
      original.replaceChildren(...nodes);
    }
    price.querySelector('.unit-price')?.classList.add('hidden');
  }

  function updateLinks(state, variant) {
    const url = variantURL(state.data, variant);
    if (!url) return;
    state.card.querySelectorAll('.card__title a, .card-wrapper__link--overlay, .js-color-swatches-link, .predictive-search__item--link').forEach(link => link.setAttribute('href', url));
    state.card.querySelectorAll('.quick-add modal-opener button[data-product-url]').forEach(button => {
      button.dataset.productUrl = url;
    });
    state.card.querySelectorAll('.card__add-to-cart input[name="id"]').forEach(input => {
      input.value = variant.id;
    });
    state.card.querySelectorAll('.card__add-to-cart button[name="add"]').forEach(button => {
      button.disabled = !variant.available;
      button.setAttribute('aria-disabled', String(!variant.available));
    });
  }

  function applyVariant(state, variant) {
    state.current = variant;
    state.card.dataset.fafSearchVariantId = variant.id;
    state.card.dataset.fafSearchVariantMatch = 'matched';
    updateImage(state, variant);
    updatePrice(state, variant);
    updateLinks(state, variant);
    syncSwatches(state);
    const options = state.data.hasOnlyDefaultVariant ? '' : variant.options.filter(value => normalize(value) !== 'default title').join(' / ');
    const parts = [options, variant.available ? '' : state.data.soldOutLabel, state.photoWarning ? state.data.colorPhotoUnavailableLabel : ''];
    notice(state, parts.filter(Boolean).join(' — '));
  }

  function notice(state, message) {
    let label = state.card.querySelector('.faf-search-variant-label');
    if (!label) {
      label = text('p', 'faf-search-variant-label', '');
      label.setAttribute('role', 'status');
      label.setAttribute('aria-live', 'polite');
      const title = state.card.querySelector('.card__title, .predictive-search__item-heading');
      if (title) title.after(label);
    }
    label.textContent = message;
    label.hidden = !label.textContent;
  }

  function bind(state) {
    const intercept = event => {
      const input = event.target instanceof Element ? event.target.closest('.js-color-swatches input[type="radio"]') : null;
      if (!input || input.closest('[data-faf-search-product]') !== state.card) return;
      event.stopImmediatePropagation();
      const variant = swatchVariant(state, input);
      if (variant) {
        if (Number(input.closest('[data-option-position]')?.dataset.optionPosition) - 1 === state.colorIndex) state.colorSelectionActive = true;
        applyVariant(state, variant);
      }
      else syncSwatches(state);
    };
    state.card.addEventListener('click', intercept, true);
    state.card.addEventListener('change', intercept, true);
  }

  function enhance(root, plan = {}) {
    if (!root?.querySelectorAll) return [];
    const cards = [...root.querySelectorAll('[data-faf-search-product]')];
    if (root.matches?.('[data-faf-search-product]')) cards.unshift(root);
    return cards.map(card => {
      let state = states.get(card);
      if (!state) {
        const script = card.querySelector('script[data-faf-search-product-data]');
        if (!script) return null;
        let data;
        try { data = JSON.parse(script.textContent); } catch { return null; }
        if (!Array.isArray(data.variants) || !data.variants.length || !Array.isArray(data.options)) return null;
        const current = data.variants.find(variant => String(variant.id) === String(data.defaultVariantId)) || preferred(data.variants);
        state = {card, data, current, colorIndex:optionIndex(data, /\b(colou?r|finish)\b/), colorSelectionActive:false};
        states.set(card, state);
        bind(state);
      }
      const selection = initialSelection(state.data, plan);
      card.dataset.fafSearchVariantMatch = selection.status;
      state.colorSelectionActive = selection.constraintTypes.includes('color');
      if (selection.variant) applyVariant(state, selection.variant);
      else if (selection.status === 'incompatible') {
        delete card.dataset.fafSearchVariantId;
        notice(state, [state.data.combinationUnavailableLabel, state.data.chooseOptionsLabel].filter(Boolean).join(' · '));
      } else if (!state.data.hasOnlyDefaultVariant && (values(plan.colors).size || values(plan.sizes).size)) {
        notice(state, state.data.chooseOptionsLabel);
      }
      return {productId:state.data.id, variantId:selection.variant?.id || null, matched:Boolean(selection.variant), variantMatch:selection.status, constraintTypes:selection.constraintTypes};
    }).filter(Boolean);
  }

  window.FAFSearchCards = Object.freeze({enhance});
})();
